import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from './useUserProfile';
import { toast } from 'sonner';
import type { ProductPlan, ProductPlanWithProduct } from '@/types/productPlans';

export function useProductPlans(productId?: string) {
  const { tenant } = useUserProfile();
  const queryClient = useQueryClient();

  const { data: plans, isLoading } = useQuery({
    queryKey: ['product_plans', tenant?.id, productId],
    queryFn: async () => {
      if (!tenant?.id) return [];
      
      let query = supabase
        .from('product_plans')
        .select(`
          *,
          products!inner(name, logo_url)
        `)
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: false });
      
      if (productId) {
        query = query.eq('product_id', productId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return data.map(plan => ({
        ...plan,
        product_name: plan.products.name,
        product_logo_url: plan.products.logo_url,
      })) as ProductPlanWithProduct[];
    },
    enabled: !!tenant?.id,
  });

  const createPlan = useMutation({
    mutationFn: async (plan: Omit<ProductPlan, 'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'created_by'>) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');
      
      const { error } = await supabase
        .from('product_plans')
        .insert({
          ...plan,
          tenant_id: tenant.id,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_plans'] });
      toast.success('Plano criado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao criar plano: ' + error.message);
    },
  });

  const updatePlan = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ProductPlan> }) => {
      const { error } = await supabase
        .from('product_plans')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_plans'] });
      toast.success('Plano atualizado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar plano: ' + error.message);
    },
  });

  const deletePlan = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('product_plans')
        .update({ is_active: false })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_plans'] });
      toast.success('Plano desativado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao desativar plano: ' + error.message);
    },
  });

  return {
    plans,
    isLoading,
    createPlan,
    updatePlan,
    deletePlan,
  };
}
